import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { useUserEvent } from '@hooks/useUserEvent';
import { EventControlComponent, EventControlRolesElement, UserEventType } from '@constants/event';
import { fetchOrganizationPermissions } from '@redux/actions/organizationActions';
import { organizationPermissionsSelector } from '@redux/selectors/organizationSelectors';
import { FULFILLED } from '@redux/types/types';
import {
  createRole as createRoleAction,
  deleteRole as deleteRoleAction,
  fetchRoles as fetchRolesAction,
  setCurrentRole as setCurrentRoleAction,
  setRolesPageActiveTab as setRolesPageActiveTabAction,
  updateRole as updateRoleAction,
} from '../redux/rolesSlice';
import { RBACRole } from '../types/roles';

const useRoles = () => {
  const dispatch = useAppDispatch();
  const { role, roles, fetchRolesStatus, createRoleStatus, rolesPageActiveTab } = useAppSelector(
    (state) => state.rolesStore,
  );
  const permissions = useAppSelector(organizationPermissionsSelector);

  const { trackUserEvent } = useUserEvent();

  const fetchPermissions = useCallback(() => {
    dispatch(fetchOrganizationPermissions());
  }, [dispatch]);

  const isPresetRole = (rbac: RBACRole) => !rbac.is_custom;

  const presetRoles = roles.filter(isPresetRole);
  const customRoles = roles.filter((item) => !isPresetRole(item));

  const fetchRoles = useCallback(() => {
    dispatch(fetchRolesAction());
  }, [dispatch]);

  const setCurrentRole = useCallback(
    (item: RBACRole | undefined) => {
      dispatch(setCurrentRoleAction(item));
    },
    [dispatch],
  );

  const setRolesPageActiveTab = useCallback(
    (tab: string) => {
      dispatch(setRolesPageActiveTabAction(tab));
    },
    [dispatch],
  );

  const createRole = useCallback(
    async (payload: RBACRole) => {
      const response = await dispatch(createRoleAction(payload));
      if (response.meta.requestStatus === FULFILLED) {
        fetchRoles();
        trackUserEvent({
          type: UserEventType.CLICK,
          control: `${EventControlComponent.ROLES}/${EventControlRolesElement.CREATE_ROLE}`,
          properties: {
            role_name: payload.role_name,
            role_description: payload.description,
            permissions: payload.permissions,
          },
        });
      }
    },
    [dispatch, fetchRoles, trackUserEvent],
  );

  const updateRole = useCallback(
    async (payload: RBACRole) => {
      const response = await dispatch(updateRoleAction(payload));
      if (response.meta.requestStatus === FULFILLED) {
        fetchRoles();
        trackUserEvent({
          type: UserEventType.CLICK,
          control: `${EventControlComponent.ROLES}/${EventControlRolesElement.UPDATE_ROLE}`,
          properties: {
            role_name: payload.role_name,
            role_description: payload.description,
            permissions: payload.permissions,
          },
        });
      }
    },
    [dispatch, fetchRoles, trackUserEvent],
  );

  const deleteRole = useCallback(
    async (payload: RBACRole) => {
      const response = await dispatch(deleteRoleAction(payload));
      if (response.meta.requestStatus === FULFILLED) {
        fetchRoles();
        trackUserEvent({
          type: UserEventType.CLICK,
          control: `${EventControlComponent.ROLES}/${EventControlRolesElement.DELETE_ROLE}`,
          properties: {
            role_name: payload.role_name,
          },
        });
      }
    },
    [dispatch, fetchRoles, trackUserEvent],
  );

  return {
    createRole,
    updateRole,
    deleteRole,
    createRoleStatus,
    permissions,
    fetchPermissions,
    customRoles,
    fetchRoles,
    fetchRolesStatus,
    isPresetRole,
    presetRoles,
    role,
    roles,
    setCurrentRole,
    rolesPageActiveTab,
    setRolesPageActiveTab,
  };
};

export default useRoles;
